import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Button, Theme } from '@havenengineering/module-shared-library/dist/components/Button';
import SelectInput from '@havenengineering/module-shared-library/dist/form/components/SelectInput';
import MultiSelectInput from '@havenengineering/module-shared-library/dist/form/components/MultiSelectInput';
import produce from 'immer';
import CheckboxInput from '@havenengineering/module-shared-library/dist/form/components/CheckboxInput';
import styles from './LettingTable.module.scss';
export var Variant;
(function (Variant) {
    Variant["BORDER"] = "Border";
    Variant["NO_BORDER"] = "NoBorder";
})(Variant || (Variant = {}));
export var PageDirection;
(function (PageDirection) {
    PageDirection["LEFT"] = "left";
    PageDirection["RIGHT"] = "right";
})(PageDirection || (PageDirection = {}));
export var SortDirection;
(function (SortDirection) {
    SortDirection["ASC"] = "asc";
    SortDirection["DESC"] = "desc";
    SortDirection["DEFAULT"] = "default";
})(SortDirection || (SortDirection = {}));
export const LettingTable = ({ columns, data, className, options, footer, highContrast = false, stickyTableHeadersOnMobile = false, variant = Variant.BORDER, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const [numOfEntries, setNumOfEntries] = useState(((_a = options === null || options === void 0 ? void 0 : options.pagination) === null || _a === void 0 ? void 0 : _a.initialEntryNumValue) || 25);
    const [filteredData, setFilteredData] = useState(((_b = options === null || options === void 0 ? void 0 : options.pagination) === null || _b === void 0 ? void 0 : _b.active) && !((_c = options === null || options === void 0 ? void 0 : options.pagination) === null || _c === void 0 ? void 0 : _c.serverSide)
        ? data.slice(0, numOfEntries)
        : data);
    const [page, setPage] = useState((((_d = options === null || options === void 0 ? void 0 : options.pagination) === null || _d === void 0 ? void 0 : _d.serverSide) && ((_e = options === null || options === void 0 ? void 0 : options.pagination) === null || _e === void 0 ? void 0 : _e.currentPage)) || 0);
    const [sortables, setSortables] = useState({});
    const [clientSideFilterState, setClientSideFilterState] = useState(columns
        .filter((c) => c.filterable)
        .reduce((filterStates, col) => (Object.assign(Object.assign({}, filterStates), { [col.field]: [] })), {}));
    const dataLength = ((_f = options === null || options === void 0 ? void 0 : options.pagination) === null || _f === void 0 ? void 0 : _f.totalCount) || data.length;
    const pageEndOffset = numOfEntries * (page + 1);
    const disabledLeftPaginate = page === 0;
    const disabledRightPaginate = pageEndOffset >= dataLength;
    useEffect(() => {
        const sortables = {};
        columns.forEach((col) => {
            sortables[col.field] = SortDirection.DEFAULT;
        });
        setSortables(sortables);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        var _a, _b, _c, _d;
        setFilteredData(((_a = options === null || options === void 0 ? void 0 : options.pagination) === null || _a === void 0 ? void 0 : _a.active) &&
            !((_b = options === null || options === void 0 ? void 0 : options.pagination) === null || _b === void 0 ? void 0 : _b.serverSide) &&
            !((_c = options === null || options === void 0 ? void 0 : options.pagination) === null || _c === void 0 ? void 0 : _c.handleNextClick) &&
            !((_d = options === null || options === void 0 ? void 0 : options.pagination) === null || _d === void 0 ? void 0 : _d.handlePrevClick)
            ? data.slice(page * numOfEntries, pageEndOffset)
            : data);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, numOfEntries, page]);
    const handlePageStep = (direction) => {
        var _a, _b, _c;
        if (direction === PageDirection.LEFT && page > 0) {
            if ((_a = options === null || options === void 0 ? void 0 : options.pagination) === null || _a === void 0 ? void 0 : _a.handlePrevClick) {
                (_b = options === null || options === void 0 ? void 0 : options.pagination) === null || _b === void 0 ? void 0 : _b.handlePrevClick(page - 1);
            }
            setPage((prev) => prev - 1);
        }
        if (direction === PageDirection.RIGHT && !(pageEndOffset >= dataLength)) {
            if ((_c = options === null || options === void 0 ? void 0 : options.pagination) === null || _c === void 0 ? void 0 : _c.handleNextClick) {
                options === null || options === void 0 ? void 0 : options.pagination.handleNextClick(page + 1);
            }
            setPage((prev) => prev + 1);
        }
    };
    const handleEntrySelect = (value) => {
        var _a, _b;
        if (value) {
            if ((_a = options === null || options === void 0 ? void 0 : options.pagination) === null || _a === void 0 ? void 0 : _a.handleEntrySelect) {
                (_b = options === null || options === void 0 ? void 0 : options.pagination) === null || _b === void 0 ? void 0 : _b.handleEntrySelect(+value);
            }
            setPage(0);
            setNumOfEntries(+value);
        }
    };
    const handleSort = (field) => {
        var _a;
        if (!((_a = options === null || options === void 0 ? void 0 : options.sortable) === null || _a === void 0 ? void 0 : _a.handleSort)) {
            // eslint-disable-next-line no-console
            console.error('Please provide a sorting handler');
            return;
        }
        let newSortState = SortDirection.DEFAULT;
        if (sortables[field] === SortDirection.DEFAULT) {
            newSortState = SortDirection.ASC;
        }
        if (sortables[field] === SortDirection.ASC) {
            newSortState = SortDirection.DESC;
        }
        const newSortableState = produce(sortables, (draftState) => {
            Object.keys(draftState).forEach((key) => {
                if (key === field) {
                    draftState[key] = newSortState;
                    return;
                }
                draftState[key] = SortDirection.DEFAULT;
            });
        });
        setSortables(newSortableState);
        options.sortable.handleSort(field, newSortState);
    };
    const handleClientSideFiltering = useCallback((field, filterValues) => {
        var _a, _b;
        if (!((_a = options === null || options === void 0 ? void 0 : options.filterable) === null || _a === void 0 ? void 0 : _a.handleFilter)) {
            // eslint-disable-next-line no-console
            console.error('Please provide a filtering handler');
            return;
        }
        const newClientSideFilterState = Object.assign(Object.assign({}, clientSideFilterState), { [field]: filterValues });
        setClientSideFilterState(newClientSideFilterState);
        (_b = options.filterable) === null || _b === void 0 ? void 0 : _b.handleFilter(newClientSideFilterState);
    }, [clientSideFilterState, options]);
    const collapsible = columns.find((col) => col.collapsibleComponent);
    return (<div className={clsx(styles.root, className, stickyTableHeadersOnMobile ? styles.stickyHeadersOnMobile : '')}>
      <div className={clsx(styles.tableWrapper, highContrast && styles.highContrast)}>
        <table className={styles[`variant${variant}`]}>
          <thead>
            <tr>
              {columns.map((col) => {
            if (!col.display) {
                return null;
            }
            return (<td key={col.field} className={styles.headerCol}>
                    <div className={styles.colWrapper}>
                      <div className={styles.titleWrapper}>
                        {col.iconSrc && <img className={styles.icon} src={col.iconSrc} alt=''/>}
                        {col.displayTitle && <span>{col.title}</span>}
                        {col.sortable && (<div className={styles.sorter} role='presentation' onClick={() => handleSort(col.field)} data-testid={`sort-icon-${col.field}`}>
                            {(sortables[col.field] === SortDirection.DEFAULT ||
                        sortables[col.field] === SortDirection.ASC) && (<img className={styles.top} src='https://www.haven.com/assets/owners/shared/dark_blue_arrow.svg' alt=''/>)}
                            {(sortables[col.field] === SortDirection.DEFAULT ||
                        sortables[col.field] === SortDirection.DESC) && (<img className={styles.bottom} src='https://www.haven.com/assets/owners/shared/dark_blue_arrow.svg' alt=''/>)}
                          </div>)}
                      </div>
                      {col.filterable && col.filterOptions && (<MultiSelectInput className={styles.filter} id={`filter-select-${col.field}`} clearLabel='Clear' value={clientSideFilterState[col.field]} onChange={(value) => handleClientSideFiltering(col.field, value)} options={col.filterOptions}/>)}
                    </div>
                  </td>);
        })}
            </tr>
          </thead>
          <tbody>
            {filteredData.map((row, index) => {
            var _a;
            return (<React.Fragment key={row.id}>
                <tr className={clsx(row.isActionRequired ? styles.highlighted : '')}>
                  {columns.map((col, columnIndex) => {
                    var _a;
                    if (!col.display) {
                        return null;
                    }
                    return (<td data-testid={`${col.field}-${index}`} key={`${col.field}-${col.title}`}>
                        <div className={clsx(styles.selectableWrapper, {
                            'fs-mask': col.maskData,
                        })}>
                          {columnIndex === 0 && ((_a = options === null || options === void 0 ? void 0 : options.selectable) === null || _a === void 0 ? void 0 : _a.handleRowSelected) ? (<CheckboxInput disabled={!row.isActionRequired} id={row.id} label='' value={row.isSelected} onChange={(value) => { var _a; return (_a = options === null || options === void 0 ? void 0 : options.selectable) === null || _a === void 0 ? void 0 : _a.handleRowSelected(row, value); }}/>) : null}{' '}
                          {col.component ? col.component(row) : row[col.field]}
                        </div>
                      </td>);
                })}
                </tr>
                {!!collapsible && !!((_a = collapsible.collapsibleComponent) === null || _a === void 0 ? void 0 : _a.call(collapsible, row)) && (<tr>
                    <td colSpan={columns.length}>{collapsible.collapsibleComponent(row)}</td>
                  </tr>)}
              </React.Fragment>);
        })}
          </tbody>
        </table>
      </div>

      {((options === null || options === void 0 ? void 0 : options.pagination) || footer) && (<div className={styles.footer}>
          {(options === null || options === void 0 ? void 0 : options.pagination) && (<div className={styles.fixedFooter}>
              {((_g = options === null || options === void 0 ? void 0 : options.pagination) === null || _g === void 0 ? void 0 : _g.active) && (<div className={styles.pagination}>
                  <Button theme={Theme.SECONDARY_THEME} onClick={() => handlePageStep(PageDirection.LEFT)} disabled={disabledLeftPaginate}>
                    <img className={clsx(styles.leftArrow, disabledLeftPaginate && styles.disabled)} src={disabledLeftPaginate
                        ? 'https://www.haven.com/assets/owners/shared/grey_arrow.svg'
                        : 'https://www.haven.com/assets/owners/shared/blue_arrow.svg'} alt=''/>
                  </Button>
                  <span>{page + 1}</span>
                  <Button theme={Theme.SECONDARY_THEME} onClick={() => handlePageStep(PageDirection.RIGHT)} disabled={disabledRightPaginate}>
                    <img className={clsx(styles.rightArrow, disabledRightPaginate && styles.disabled)} src={disabledRightPaginate
                        ? 'https://www.haven.com/assets/owners/shared/grey_arrow.svg'
                        : 'https://www.haven.com/assets/owners/shared/blue_arrow.svg'} alt=''/>
                  </Button>
                </div>)}

              {((_h = options === null || options === void 0 ? void 0 : options.pagination) === null || _h === void 0 ? void 0 : _h.enableEntryNumSelector) && (<div className={styles.numOfEntries}>
                  <span>Show:</span>
                  <SelectInput id='numOfEntries' onChange={handleEntrySelect} value={numOfEntries.toString()} className={styles.select} options={[
                        {
                            label: '10',
                            value: '10',
                        },
                        {
                            label: '25',
                            value: '25',
                        },
                        {
                            label: '50',
                            value: '50',
                        },
                        {
                            label: '100',
                            value: '100',
                        },
                        {
                            label: 'All',
                            value: dataLength.toString(),
                        },
                    ]}/>
                </div>)}
            </div>)}
          {footer && footer()}
        </div>)}
    </div>);
};
